import QRCodeStyling from "qr-code-styling";
import jsPDF from "jspdf";

const validateUrl = (url) => {
    if (!url || typeof url !== 'string' || url.trim() === '') {
        throw new Error('URL is required and must be a valid string');
    }

    try {
        new URL(url);
    } catch (error) {
        throw new Error('Invalid URL format provided');
    }

    return url.trim();
};

const validateSize = (size) => {
    if (size !== null && size !== undefined) {
        const sizeNum = parseInt(size, 10);
        if (isNaN(sizeNum) || sizeNum < 50 || sizeNum > 2000) {
            throw new Error('Size must be a number between 50 and 2000 pixels');
        }
        return sizeNum;
    }
    return 300;
};

const validateColor = (color, defaultColor = '#000000') => {
    if (color !== null && color !== undefined) {
        if (typeof color !== 'string') {
            throw new Error('Color must be a string');
        }

        const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
        if (!hexRegex.test(color)) {
            throw new Error('Color must be a valid hex color (e.g., #000000)');
        }

        return color;
    }
    return defaultColor;
};

const validateGradientColors = (gradientColors) => {
    if (gradientColors && Array.isArray(gradientColors)) {
        if (gradientColors.length < 2) {
            throw new Error('Gradient colors must have at least 2 colors');
        }

        return gradientColors.map(color => validateColor(color));
    }
    return undefined;
};

const validateLogoUrl = (logoUrl) => {
    if (logoUrl !== null && logoUrl !== undefined) {
        if (typeof logoUrl !== 'string') {
            throw new Error('Logo URL must be a string');
        }

        if (logoUrl.trim() !== '') {
            try {
                new URL(logoUrl);
                return logoUrl.trim();
            } catch (error) {
                throw new Error('Invalid logo URL format');
            }
        }
    }
    return undefined;
};

const validateLogoSize = (logoSize) => {
    if (logoSize !== null && logoSize !== undefined) {
        const sizeNum = parseFloat(logoSize);
        if (isNaN(sizeNum) || sizeNum < 0 || sizeNum > 1) {
            throw new Error('Logo size must be a number between 0 and 1');
        }
        return sizeNum;
    }
    return 0.2;
};

const validateFormat = (format) => {
    const validFormats = ['svg', 'png', 'jpg', 'jpeg', 'pdf'];
    if (format && !validFormats.includes(format.toLowerCase())) {
        throw new Error(`Unsupported format. Supported formats: ${validFormats.join(', ')}`);
    }
    return format ? format.toLowerCase() : 'png';
};

const validateDotType = (dotType) => {
    const validDotTypes = ['square', 'dots', 'rounded', 'classy', 'extra-rounded'];
    if (dotType && !validDotTypes.includes(dotType)) {
        throw new Error(`Invalid dot type. Valid types: ${validDotTypes.join(', ')}`);
    }
    return dotType || 'square';
};

const validateEyeShape = (eyeShape) => {
    const validEyeShapes = ['square', 'circle', 'rounded'];
    if (eyeShape && !validEyeShapes.includes(eyeShape)) {
        throw new Error(`Invalid eye shape. Valid shapes: ${validEyeShapes.join(', ')}`);
    }
    return eyeShape || 'square';
};

const validateGradientType = (gradientType) => {
    const validGradientTypes = ['linear', 'radial'];
    if (gradientType && !validGradientTypes.includes(gradientType)) {
        throw new Error(`Invalid gradient type. Valid types: ${validGradientTypes.join(', ')}`);
    }
    return gradientType || 'linear';
};

const validateGradientRotation = (gradientRotation) => {
    if (gradientRotation !== null && gradientRotation !== undefined) {
        const rotationNum = parseFloat(gradientRotation);
        if (isNaN(rotationNum) || rotationNum < 0 || rotationNum > 360) {
            throw new Error('Gradient rotation must be a number between 0 and 360 degrees');
        }
        return rotationNum;
    }
    return 0;
};

const validateLogoMargin = (logoMargin) => {
    if (logoMargin !== null && logoMargin !== undefined) {
        const marginNum = parseInt(logoMargin, 10);
        if (isNaN(marginNum) || marginNum < 0) {
            throw new Error('Logo margin must be a non-negative number');
        }
        return marginNum;
    }
    return 0;
};

const validateQRCodeOptions = (url, options = {}) => {
    const validUrl = validateUrl(url);
    const validSize = validateSize(options.size);
    const validColor = validateColor(options.color);
    const validBackgroundColor = validateColor(options.backgroundColor, '#FFFFFF');
    const validFormat = validateFormat(options.format);
    const validDotType = validateDotType(options.dotType);
    const validEyeShape = validateEyeShape(options.eyeShape);
    const validEyeColor = validateColor(options.eyeColor);
    const validGradientType = validateGradientType(options.gradientType);
    const validGradientRotation = validateGradientRotation(options.gradientRotation);
    const validGradientColors = validateGradientColors(options.gradientColors);
    const validEyeGradientType = validateGradientType(options.eyeGradientType);
    const validEyeGradientColors = validateGradientColors(options.eyeGradientColors);
    const validLogoUrl = validateLogoUrl(options.logoUrl);
    const validLogoMargin = validateLogoMargin(options.logoMargin);
    const validLogoSize = validateLogoSize(options.logoSize);
    const validLogoCrossOrigin = options.logoCrossOrigin || 'anonymous';
    const validHideBackgroundDots = Boolean(options.hideBackgroundDots);

    return {
        url: validUrl,
        size: validSize,
        color: validColor,
        backgroundColor: validBackgroundColor,
        format: validFormat,
        dotType: validDotType,
        eyeShape: validEyeShape,
        eyeColor: validEyeColor,
        gradientType: validGradientType,
        gradientRotation: validGradientRotation,
        gradientColors: validGradientColors,
        eyeGradientType: validEyeGradientType,
        eyeGradientColors: validEyeGradientColors,
        logoUrl: validLogoUrl,
        logoMargin: validLogoMargin,
        logoSize: validLogoSize,
        logoCrossOrigin: validLogoCrossOrigin,
        hideBackgroundDots: validHideBackgroundDots,
    };
};

const handleQRCodeError = (error, operation = 'QR code operation') => {
    if (error.message.includes('URL')) {
        throw new Error('Invalid URL provided. Please check the URL format and try again.');
    }

    if (error.message.includes('format')) {
        throw new Error('Unsupported file format. Please choose a supported format.');
    }

    if (error.message.includes('size') || error.message.includes('dimension')) {
        throw new Error('Invalid size specified. Please use a size between 50 and 2000 pixels.');
    }

    if (error.message.includes('color')) {
        throw new Error('Invalid color format. Please use valid hex colors (e.g., #000000).');
    }

    if (error.message.includes('logo')) {
        throw new Error('Invalid logo configuration. Please check logo URL and settings.');
    }

    if (error.message.includes('gradient')) {
        throw new Error('Invalid gradient configuration. Please check gradient settings.');
    }

    if (error.message.includes('fetch') || error.message.includes('network')) {
        throw new Error('Failed to load resources. Please check your internet connection and try again.');
    }

    if (error.message.includes('blob') || error.message.includes('createObjectURL')) {
        throw new Error('Failed to generate file. Please try again or use a different format.');
    }

    if (error.message.includes('pdf') || error.message.includes('jsPDF')) {
        throw new Error('Failed to generate PDF. Please try a different format or try again.');
    }

    throw new Error(`Failed to generate QR code: ${error.message}`);
};

export const generateQRCode = async (url, options = {}) => {
    try {
        const validOptions = validateQRCodeOptions(url, options);

        const gradientOptions = validOptions.gradientColors && validOptions.gradientColors.length
            ? {
                type: validOptions.gradientType,
                rotation: validOptions.gradientRotation,
                colorStops: validOptions.gradientColors,
            }
            : undefined;

        const eyeGradientOptions = validOptions.eyeGradientColors && validOptions.eyeGradientColors.length
            ? {
                type: validOptions.eyeGradientType,
                colorStops: validOptions.eyeGradientColors,
            }
            : undefined;

        const qrCode = new QRCodeStyling({
            width: validOptions.size,
            height: validOptions.size,
            data: validOptions.url,
            image: validOptions.logoUrl,
            dotsOptions: {
                color: validOptions.color,
                type: validOptions.dotType,
                gradient: gradientOptions,
            },
            backgroundOptions: {
                color: validOptions.backgroundColor,
            },
            cornersSquareOptions: {
                type: validOptions.eyeShape,
                color: validOptions.eyeColor,
                gradient: eyeGradientOptions,
            },
            imageOptions: {
                margin: validOptions.logoMargin,
                size: validOptions.logoSize,
                crossOrigin: validOptions.logoCrossOrigin,
                hideBackgroundDots: validOptions.hideBackgroundDots,
            },
        });

        let resultUrl;

        if (validOptions.format === 'svg') {
            const svgBlob = await qrCode.getRawData("svg");
            resultUrl = URL.createObjectURL(svgBlob);
        } else if (['png', 'jpg', 'jpeg'].includes(validOptions.format)) {
            const imageBlob = await qrCode.getRawData(validOptions.format);
            resultUrl = URL.createObjectURL(imageBlob);
        } else if (validOptions.format === 'pdf') {
            const pngBlob = await qrCode.getRawData('png');
            const pdf = new jsPDF();
            const imageUrl = URL.createObjectURL(pngBlob);

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const qrSize = Math.min(pdfWidth, pdfHeight) * 0.8; // 80% of page size
            const x = (pdfWidth - qrSize) / 2;
            const y = (pdfHeight - qrSize) / 2;

            pdf.addImage(imageUrl, 'PNG', x, y, qrSize, qrSize);
            const pdfBlob = pdf.output('blob');
            resultUrl = URL.createObjectURL(pdfBlob);

            URL.revokeObjectURL(imageUrl);
        }

        return resultUrl;
    } catch (error) {
        handleQRCodeError(error, 'Generating QR code');
    }
};

export const cleanupQRCodeUrl = (url) => {
    if (url && typeof url === 'string' && url.startsWith('blob:')) {
        try {
            URL.revokeObjectURL(url);
        } catch (error) {
            // Silently handle cleanup errors
        }
    }
};

export const downloadQRCode = async (url, options = {}) => {
    try {
        const validOptions = validateQRCodeOptions(url, options);
        const qrCodeUrl = await generateQRCode(url, validOptions);

        if (!qrCodeUrl) {
            throw new Error('Failed to generate QR code for download');
        }

        const link = document.createElement('a');
        link.href = qrCodeUrl;
        link.download = `qr-code.${validOptions.format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
            cleanupQRCodeUrl(qrCodeUrl);
        }, 1000);

        return true;
    } catch (error) {
        handleQRCodeError(error, 'Downloading QR code');
    }
};
